/* eslint-disable prefer-destructuring */
import { Toast as JojoToast } from '@jojo-design/mobile';
import { Toast } from 'antd-mobile';
import { t } from 'i18next';
import React, { useMemo, useReducer } from 'react';
import { Cookies } from 'react-cookie';

import { LoadHandle, LoadStatus } from '@/components/PageLoading';
import {
  getAvailableCoupons,
  getCouponActivityDetail,
  getProductCoupon,
  getProductCouponList,
  getProductsByLinkIds,
  getPromotionCoupon,
  getPromotionCouponList,
  getValidCoupons,
  getVoucherList,
  postPickCoupon
} from '@/services/coupon';
import { sensElementView } from '@/utils/sensors';

const cookies = new Cookies();

const _trans = t;

const initialState = {
  loadStatus: {},
  activityDetail: null, // 活动
  productsInActivity: [], // 优惠券活动详情页推荐的商品
  couponInProduct: null, // 商品详情页最优优惠券
  couponInProductList: [], // 商品详情页弹框优惠券列表
  couponInProductModal: {
    visible: false
  },
  couponInMine: [
    {
      title: _trans('models.available', '可用'),
      status: 1,
      data: [],
      pageNum: 1,
      refreshing: false,
      hasMore: true
    },
    {
      title: _trans('models.expired', '已失效'),
      status: 2,
      data: [],
      pageNum: 1,
      refreshing: false,
      hasMore: true
    }
  ],
  couponInUse: [
    {
      title: _trans('models.available', '可用'),
      status: 1,
      data: [],
      refreshing: false
    },
    {
      title: _trans('models.unavailable', '不可用'),
      status: 2,
      data: [],
      refreshing: false
    }
  ],
  voucherInList: [
    {
      title: '有效',
      status: 1,
      data: [],
      pageNum: 1,
      refreshing: false,
      hasMore: true
    },
    {
      title: '已使用',
      status: 2,
      data: [],
      pageNum: 1,
      refreshing: false,
      hasMore: true
    },
    {
      title: '已过期',
      status: 3,
      data: [],
      pageNum: 1,
      refreshing: false,
      hasMore: true
    }
  ],
  voucherInUse: [
    {
      title: _trans('models.available', '可用'),
      status: 1,
      data: [],
      pageNum: 1,
      refreshing: false,
      hasMore: true,
      totalRow: 0
    },
    {
      title: _trans('models.unavailable', '不可用'),
      status: 2,
      data: [],
      pageNum: 1,
      refreshing: false,
      hasMore: true
    }
  ]
};

const initialContext = {
  state: initialState,
  dispatch: () => null
};

// http接口审核 格式化数据 将pickState 的数据改为枚举类型
// 1-正常,2-活动以结束,3-已抢光,4-非活动对象
const pickStateMap = {
  NORMAL: 1,
  END: 2,
  STOCK_OVER: 3,
  NOT_ACTIVITY_USER: 4,
  NO_RECEIVABLE_COUPON: 5,
  CLAIMED: 6
};

/**
 * 同步操作
 * @param state
 * @param action
 */
const reducers = (state: any, action: { type: string; payload: any }) => {
  switch (action.type) {
    case 'loadStatus':
      return { ...state, loadStatus: action.payload };
    case 'update':
      return { ...state, ...action.payload };
    case 'updateMine':
      return { ...state, couponInMine: [...action.payload] };
    case 'updateUse':
      return { ...state, couponInUse: [...action.payload] };
    default:
      throw new Error(`${_trans('models.wrongType', '错误的type：')}${action.type}`);
  }
};

/**
 * 异步操作dispatch
 * @param dispatch
 * @param state
 */
const dispatchMiddleWare = (dispatch: any, state: any) => async (action: any) => {
  let resp;
  let resultCode;
  let data;

  const recursionDispatch = dispatchMiddleWare(dispatch, state);

  const isPromotion = action.payload?.linkCode?.startsWith('NL');
  switch (action.type) {
    case 'fetch':
      if (!state.activityDetail || !state.activityDetail.bgColor) {
        dispatch({ type: 'loadStatus', payload: { status: LoadStatus.Loading } });
      }
      resp = await getCouponActivityDetail({ activityId: action.payload });
      resultCode = resp.resultCode;
      data = resp.data;
      if (resultCode === 200 && data) {
        data.pickState = pickStateMap[data?.pickState] || data.pickState;
        dispatch({
          type: 'update',
          payload: {
            activityDetail: data as CouponActivityInfo
          }
        });
      }

      dispatch({ type: 'loadStatus', payload: LoadHandle(resp) });

      break;
    case 'fetchGoods':
      resp = await getProductsByLinkIds(action.payload);
      resultCode = resp.resultCode;
      data = resp.data;

      if (resultCode === 200 && data) {
        dispatch({ type: 'update', payload: { productsInActivity: data as ProductsInActivity[] } });
      } else {
        Toast.info(resp.errorMsg || _trans('models.error', '出错了'), 1);
      }

      break;

    case 'pickCouponReloadActivity':
      resp = await postPickCoupon(action.payload);
      resultCode = resp.resultCode;
      data = resp.data;

      if (resultCode === 200) {
        sensElementView({
          $element_name: '优惠券活动页-领取成功',
          $element_content: action.payload.activityCode
        });
        Toast.info(_trans('models.ReceivedSuccessfully', '领取成功'), 2, () => {
          const { activityCode } = action.payload;
          recursionDispatch({ type: 'fetch', payload: activityCode });
        });
      } else {
        sensElementView({
          $element_name: '优惠券活动页-领取失败',
          $element_content: action.payload.activityCode,
          custom_state: resp.errorMsg || ''
        });
        Toast.info(resp.errorMsg || _trans('models.error', '出错了'), 1);
      }

      break;

    case 'pickCouponReloadInProduct':
      Toast.loading(_trans('models.loading', '正在加载...'), 999);
      resp = await postPickCoupon(action.payload);
      Toast.hide();

      resultCode = resp.resultCode;
      data = resp.data;

      if (resultCode === 200) {
        JojoToast.show({
          maskStyle: { zIndex: 1000 },
          icon: 'success',
          content: _trans('models.ReceivedSuccessfully', '领取成功')
        });
        const { productId, linkCode, skuIds } = action.payload;
        recursionDispatch({
          type: 'fetchDetailCouponList',
          payload: { productId, linkCode, skuIds, displayPosition: 'MORE_LIST' }
        });
        recursionDispatch({ type: 'fetchDetailCoupon', payload: { productId, linkCode, skuIds } });
      } else if (resultCode === 500) {
        Toast.info(_trans('models.error', resp.errorMsg || '出错了'), 1);
      } else {
        Toast.info(resp.errorMsg || _trans('models.error', '出错了'), 1);
      }

      return resp;

    case 'fetchDetailCoupon':
      if (isPromotion) {
        resp = await getPromotionCouponList(action.payload);
        resultCode = resp.resultCode;
        data = resp.data;
        if (resultCode === 200 && data && data.length) {
          data[0].pickState = pickStateMap?.[data?.[0]?.pickState] || data?.[0].pickState;
          dispatch({
            type: 'update',
            payload: { couponInProduct: data[0] as CouponInfo }
          });
        }
      } else {
        resp = await getProductCoupon(action.payload);
        resultCode = resp.resultCode;
        data = resp.data;

        if (resultCode === 200 && data) {
          dispatch({ type: 'update', payload: { couponInProduct: data[0] as CouponInfo } });
        }
      }
      break;

    case 'fetchDetailCouponList':
      const requestFunc = isPromotion ? getPromotionCouponList : getProductCouponList;
      resp = await requestFunc(action.payload);
      resultCode = resp.resultCode;
      data = resp.data;

      if (resultCode === 200 && data && data.length) {
        data[0].pickState = pickStateMap?.[data?.[0]?.pickState] || data?.[0].pickState;
        dispatch({ type: 'update', payload: { couponInProductList: data as CouponInfo[] } });
      } else {
        Toast.info(resp.errorMsg || _trans('models.error', '出错了'), 1);
      }

      return resp;

    case 'fetchValidCouponList': {
      const { status, pageNum, pageSize } = action.payload;

      const newTabData = [...state.couponInMine];
      const index = state.couponInMine.findIndex((c: any) => status === c.status);
      const item = state.couponInMine[index];
      const noMore = item.data.length && item.data.length % 20;
      // 没有更多，正在加载时
      if (index === -1 || noMore || newTabData[index].refreshing) return null;

      newTabData[index].refreshing = true;
      dispatch({ type: 'updateMine', payload: newTabData });

      resp = await getValidCoupons({
        validStatus: status,
        pageNum,
        pageSize
      });
      newTabData[index].refreshing = false;

      resultCode = resp.resultCode;
      data = resp.data;

      if (resultCode === 200 && Array.isArray(data.items)) {
        if (pageNum === 1) {
          newTabData[index].data = data.items;
        } else {
          newTabData[index].data.push(...data.items);
        }
        newTabData[index].status = status;
        newTabData[index].pageNum = pageNum;

        if (!data.items || data.items.length < pageSize) {
          newTabData[index].hasMore = false;
        }
      }
      dispatch({ type: 'updateMine', payload: newTabData });

      return resp;
    }

    case 'fetchAvailableCouponList': {
      const {
        productId,
        skuId,
        status,
        productLinkId,
        skuQuantity,
        banForUsing,
        skuSubSectionModeDetailId,
        couponTemplateId,
        promPrice,
        isPromotion: innerIsPromotion = false,
        sellerIpId
      } = action.payload;

      const newTabData = [...state.couponInUse];
      const index = state.couponInUse.findIndex((c: any) => status === c.status);
      if (index === -1) return null;
      newTabData[index].refreshing = true;
      dispatch({ type: 'updateUse', payload: newTabData });
      const uid = cookies.get('uid') || 1;
      const request = JSON.parse(innerIsPromotion) ? getPromotionCoupon : getAvailableCoupons;
      resp = await request({
        skuId,
        productId,
        skuQuantity,
        productLinkId,
        banForUsing,
        couponTemplateId,
        availableStatus: status,
        skuSubSectionModeDetailId,
        promPrice,
        userId: uid,
        sellerIpId
      });

      newTabData[index].refreshing = false;

      resultCode = resp.resultCode;
      data = resp.data;

      if (resultCode === 200 && Array.isArray(data)) {
        newTabData[index].data = data;
        newTabData[index].status = status;
      }
      dispatch({ type: 'updateUse', payload: newTabData });

      return resp;
    }

    // 代金券相关操作
    // 获取代金券列表
    case 'fetchVoucherList': {
      const { status, pageNum, pageSize } = action.payload;
      const newTabData = [...state.voucherInList];
      const index = state.voucherInList.findIndex((c: any) => status === c.status);
      const item = state.voucherInList[index];
      const noMore = item.data.length && item.data.length % 20;
      // 没有更多，正在加载时
      if (index === -1 || noMore || newTabData[index].refreshing) return null;

      newTabData[index].refreshing = true;
      dispatch({ type: 'update', payload: { voucherInList: newTabData } });
      let assetsType = '';
      if (status === 1) {
        assetsType = 'VALID';
      } else if (status === 2) {
        assetsType = 'USED';
      } else if (status === 3) {
        assetsType = 'EXPIRED';
      }
      resp = await getVoucherList({
        pageNum,
        pageSize,
        assetsType
      });
      newTabData[index].refreshing = false;

      resultCode = resp.resultCode;
      data = resp.data;

      if (resultCode === 200 && Array.isArray(data.items)) {
        if (pageNum === 1) {
          newTabData[index].data = data.items;
        } else {
          newTabData[index].data.push(...data.items);
        }
        newTabData[index].status = status;
        newTabData[index].pageNum = pageNum;

        if (!data.items || data.items.length < pageSize) {
          newTabData[index].hasMore = false;
        }
      }
      dispatch({ type: 'update', payload: { voucherInList: newTabData } });

      return resp;
    }
    // 获取可用 | 不可用 代金券列表
    case 'fetchUseVoucherList': {
      const { status, pageNum, skuId, linkCode, pageSize } = action.payload;
      const newTabData = [...state.voucherInUse];
      const index = state.voucherInUse.findIndex((c: any) => status === c.status);
      const item = state.voucherInUse[index];
      const noMore = item.data.length && item.data.length % pageSize;
      // 没有更多，正在加载时
      // console.log(111115, index, noMore, newTabData[index].refreshing);

      if (index === -1 || noMore || newTabData[index].refreshing) return null;

      newTabData[index].refreshing = true;
      dispatch({ type: 'update', payload: { voucherInUse: newTabData } });
      let assetsType = '';
      if (status === 1) {
        assetsType = 'AVAILABLE';
      } else if (status === 2) {
        assetsType = 'UNAVAILABLE';
      }
      // const params = ;
      const res = await getVoucherList({
        pageNum,
        pageSize,
        assetsType,
        ...(skuId
          ? {
              skuId
            }
          : {
              linkCode
            })
      });
      newTabData[index].refreshing = false;

      resultCode = res.resultCode;
      data = res.data;

      if (resultCode === 200 && Array.isArray(data.items)) {
        if (pageNum === 1) {
          newTabData[index].data = data.items;
        } else {
          newTabData[index].data.push(...data.items);
        }
        newTabData[index].status = status;
        newTabData[index].pageNum = pageNum;
        newTabData[index].totalRow = data?.totalRow;

        if (!data.items || data.items.length < pageSize) {
          newTabData[index].hasMore = false;
        }
      }
      dispatch({ type: 'update', payload: { voucherInUse: newTabData } });

      break;
    }

    default:
      dispatch(action);
      break;
  }
  return resp;
};

// 优惠券store
const CouponContext = React.createContext<ContextWithReducer<any>>(initialContext);

const CouponProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducers, initialState);

  const value = useMemo(
    () => ({
      state,
      dispatch: dispatchMiddleWare(dispatch, state)
    }),
    [state]
  );

  return <CouponContext.Provider value={value}>{children}</CouponContext.Provider>;
};

export { CouponContext, CouponProvider };
